import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../../config/firebase";
import { doc, collection, getDocs, deleteDoc, setDoc, updateDoc, deleteField, query, orderBy, limit } from "firebase/firestore";
import { useSubscription } from "../../../../hooks/useSubscription";
import { useProjectDuplicate } from "../../../../hooks/useProjectDuplicate";

type Props = {
  className: string;
  userID: string;
  onProjectsLoaded?: (count: number) => void;
  readOnly?: boolean;
};

interface Project {
  projectID: string;
  projectTitle?: string;
  projectDescription?: string;
  projectProgress?: number;
  lastActiveAt?: string;
  archivedAt?: string;
  projectStatus?: string;
  tags?: string[];
  pinned?: boolean;
  healthAlert?: boolean;
  teamId?: string | null;
  collaboratorUIDs?: string[];
}

type ProjectStatus = "Ideating" | "Validating" | "Building" | "Launched";

const STATUS_OPTIONS: { key: ProjectStatus; color: string; bg: string }[] = [
  { key: "Ideating",   color: "#475569", bg: "#F1F5F9" },
  { key: "Validating", color: "#3B82F6", bg: "#DBEAFE" },
  { key: "Building",   color: "#8B5CF6", bg: "#EDE9FE" },
  { key: "Launched",   color: "#10B981", bg: "#D1FAE5" },
];

const getStatusStyle = (status: string) =>
  STATUS_OPTIONS.find((s) => s.key === status) ?? { color: "#475569", bg: "#F1F5F9" };

type FilterTier = "all" | "strong" | "needs-work" | "early";

interface FilterPreset {
  id: string;
  name: string;
  filterTier: FilterTier;
  tagFilter: string | null;
}

const MAX_PRESETS = 6;

const loadPresets = (userID: string): FilterPreset[] => {
  try {
    const raw = localStorage.getItem(`bf_filter_presets_${userID}`);
    return raw ? (JSON.parse(raw) as FilterPreset[]) : [];
  } catch {
    return [];
  }
};

const savePresetsToStorage = (userID: string, presets: FilterPreset[]): void => {
  try {
    localStorage.setItem(`bf_filter_presets_${userID}`, JSON.stringify(presets));
  } catch {
    // Non-critical
  }
};

// Human-readable relative timestamps (mirrors timeAgo in ProjectDashboardPage + GenericStepForm)
const timeAgo = (isoString: string): string => {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "yesterday";
  if (diffD < 30) return `${diffD}d ago`;
  return `${Math.floor(diffD / 30)}mo ago`;
};

/**
 * Get score colour tokens from DESIGN.md based on progress value.
 * score-high ≥70%, score-medium 40-69%, score-low <40%
 */
const getScoreStyle = (progress: number) => {
  if (progress >= 70)
    return {
      color: "#10B981",
      bg: "#D1FAE5",
      label: "Strong",
    };
  if (progress >= 40)
    return {
      color: "#F59E0B",
      bg: "#FEF3C7",
      label: "Needs Work",
    };
  return {
    color: "#EF4444",
    bg: "#FEE2E2",
    label: "Early Stage",
  };
};

const FILTER_OPTIONS: { key: FilterTier; label: string }[] = [
  { key: "all", label: "All" },
  { key: "strong", label: "Strong" },
  { key: "needs-work", label: "Needs Work" },
  { key: "early", label: "Early Stage" },
];

const TAG_OPTIONS: { key: string; color: string; bg: string }[] = [
  { key: "B2B",         color: "#3B82F6", bg: "#DBEAFE" },
  { key: "B2C",         color: "#8B5CF6", bg: "#EDE9FE" },
  { key: "SaaS",        color: "#10B981", bg: "#D1FAE5" },
  { key: "Marketplace", color: "#EC4899", bg: "#FCE7F3" },
  { key: "Mobile App",  color: "#F59E0B", bg: "#FEF3C7" },
  { key: "Side Project",color: "#475569", bg: "#F1F5F9" },
  { key: "Agency",      color: "#4F46E5", bg: "#EEF2FF" },
  { key: "Hardware",    color: "#F97316", bg: "#FFEDD5" },
];

const getTagStyle = (tag: string) =>
  TAG_OPTIONS.find((t) => t.key === tag) ?? { color: "#475569", bg: "#F1F5F9" };

const ProjectList: React.FC<Props> = ({ className, userID, onProjectsLoaded, readOnly = false }) => {
  const navigate = useNavigate();
  const { tier, projectLimit } = useSubscription();
  const { duplicateProject, isDuplicating } = useProjectDuplicate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [openMenuID, setOpenMenuID] = useState<string | null>(null);
  const [duplicatingID, setDuplicatingID] = useState<string | null>(null);
  const [deletingID, setDeletingID] = useState<string | null>(null);
  const [archivingID, setArchivingID] = useState<string | null>(null);
  const [confirmDeleteProject, setConfirmDeleteProject] = useState<Project | null>(null);
  const [pinningID, setPinningID] = useState<string | null>(null);
  const [statusMenuID, setStatusMenuID] = useState<string | null>(null);
  const [tagMenuID, setTagMenuID] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTier, setFilterTier] = useState<FilterTier>("all");
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetInputValue, setPresetInputValue] = useState("");
  const menuRef = useRef<HTMLDivElement | null>(null);
  const presetInputRef = useRef<HTMLInputElement | null>(null);
  const [healthAlerts, setHealthAlerts] = useState<Record<string, boolean>>({});
  const [scoreDeltas, setScoreDeltas] = useState<Record<string, number>>({});

  const fetchProjects = useCallback(async (uid: string): Promise<Project[]> => {
    const userRef = doc(db, "users", uid);
    const snapshot = await getDocs(collection(userRef, "projects"));
    return snapshot.docs.map((docSnapshot) => ({
      ...(docSnapshot.data() as Omit<Project, "projectID">),
      projectID: docSnapshot.id,
    }));
  }, []);

  useEffect(() => {
    if (!userID) return;
    setLoading(true);
    fetchProjects(userID)
      .then((data) => {
        setProjects(data);
        onProjectsLoaded?.(data.filter((p) => !p.archivedAt).length);
      })
      .catch(() => {
        setProjects([]);
        onProjectsLoaded?.(0);
      })
      .finally(() => setLoading(false));
  }, [userID, fetchProjects, onProjectsLoaded]);

  // Load presets from localStorage when userID is known
  useEffect(() => {
    if (!userID) return;
    setPresets(loadPresets(userID));
  }, [userID]);

  // Focus preset name input when it appears
  useEffect(() => {
    if (savingPreset && presetInputRef.current) {
      presetInputRef.current.focus();
    }
  }, [savingPreset]);

  // Fetch last 2 score history entries per active project to detect drops >5 points
  useEffect(() => {
    if (!userID || projects.length === 0) return;
    const activeProjects = projects.filter((p) => !p.archivedAt);
    if (activeProjects.length === 0) return;

    Promise.all(
      activeProjects.map(async (project) => {
        try {
          const histRef = collection(db, "users", userID, "projects", project.projectID, "scoreHistory");
          const q = query(histRef, orderBy("timestamp", "desc"), limit(2));
          const snap = await getDocs(q);
          if (snap.size < 2) return { projectID: project.projectID, alert: false, delta: undefined };
          const recent = (snap.docs[0]!.data() as { overallScore: number }).overallScore;
          const previous = (snap.docs[1]!.data() as { overallScore: number }).overallScore;
          const delta = recent - previous;
          return { projectID: project.projectID, alert: delta < -5, delta };
        } catch {
          return { projectID: project.projectID, alert: false, delta: undefined };
        }
      })
    ).then((results) => {
      const alertMap: Record<string, boolean> = {};
      const deltaMap: Record<string, number> = {};
      results.forEach(({ projectID, alert, delta }) => {
        alertMap[projectID] = alert;
        if (delta !== undefined) deltaMap[projectID] = delta;
      });
      setHealthAlerts(alertMap);
      setScoreDeltas(deltaMap);
    }).catch(() => {
      // Non-critical — badges just won't show
    });
  }, [userID, projects]);

  const clearFilters = () => {
    setSearchQuery("");
    setFilterTier("all");
    setActiveTagFilter(null);
  };

  const handleApplyPreset = (preset: FilterPreset) => {
    const isActive =
      filterTier === preset.filterTier && activeTagFilter === preset.tagFilter;
    if (isActive) {
      // Toggle off — reset to defaults
      setFilterTier("all");
      setActiveTagFilter(null);
    } else {
      setFilterTier(preset.filterTier);
      setActiveTagFilter(preset.tagFilter);
    }
  };

  const handleSavePreset = () => {
    const name = presetInputValue.trim();
    if (!name) return;
    const newPreset: FilterPreset = {
      id: crypto.randomUUID(),
      name,
      filterTier,
      tagFilter: activeTagFilter,
    };
    const updated = [...presets, newPreset];
    setPresets(updated);
    savePresetsToStorage(userID, updated);
    setSavingPreset(false);
    setPresetInputValue("");
  };

  const handleDeletePreset = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = presets.filter((p) => p.id !== id);
    setPresets(updated);
    savePresetsToStorage(userID, updated);
  };

  const handleNavigate = (project: Project) => {
    navigate("/project", {
      state: {
        projectTitle: project.projectTitle,
        projectID: project.projectID,
        projectDescription: project.projectDescription,
        userID,
      },
    });
  };

  const handleDuplicate = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuID(null);
    setDuplicatingID(project.projectID);
    const result = await duplicateProject(userID, project.projectID, project.projectTitle ?? "Untitled");
    if (result) {
      fetchProjects(userID).then((data) => {
        setProjects(data);
        onProjectsLoaded?.(data.filter((p) => !p.archivedAt).length);
      }).catch(() => {
        setProjects([]);
      });
    }
    setDuplicatingID(null);
  };

  const handleDeleteRequest = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuID(null);
    setConfirmDeleteProject(project);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteProject) return;
    const projectID = confirmDeleteProject.projectID;
    setConfirmDeleteProject(null);
    setDeletingID(projectID);
    try {
      const userRef = doc(db, "users", userID);
      // Delete all docs in the progress sub-collection
      const progressRef = collection(userRef, "projects", projectID, "progress");
      const progressDocs = await getDocs(progressRef);
      await Promise.all(progressDocs.docs.map((d) => deleteDoc(d.ref)));
      // Delete the project doc itself
      await deleteDoc(doc(userRef, "projects", projectID));
      const updated = projects.filter((p) => p.projectID !== projectID);
      setProjects(updated);
      onProjectsLoaded?.(updated.filter((p) => !p.archivedAt).length);
    } catch {
      // Non-critical — project still shows until refresh
    } finally {
      setDeletingID(null);
    }
  };

  const handleArchive = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuID(null);
    setArchivingID(project.projectID);
    try {
      const projectRef = doc(db, "users", userID, "projects", project.projectID);
      await setDoc(projectRef, { archivedAt: new Date().toISOString() }, { merge: true });
      setProjects((prev) =>
        prev.map((p) =>
          p.projectID === project.projectID ? { ...p, archivedAt: new Date().toISOString() } : p
        )
      );
    } catch {
      // Non-critical
    } finally {
      setArchivingID(null);
    }
  };

  const handleUnarchive = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuID(null);
    setArchivingID(project.projectID);
    try {
      const projectRef = doc(db, "users", userID, "projects", project.projectID);
      await updateDoc(projectRef, { archivedAt: deleteField() });
      setProjects((prev) =>
        prev.map((p) =>
          p.projectID === project.projectID ? { ...p, archivedAt: undefined } : p
        )
      );
    } catch {
      // Non-critical
    } finally {
      setArchivingID(null);
    }
  };

  const handlePin = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuID(null);
    setPinningID(project.projectID);
    try {
      const projectRef = doc(db, "users", userID, "projects", project.projectID);
      await updateDoc(projectRef, { pinned: true });
      setProjects((prev) =>
        prev.map((p) => p.projectID === project.projectID ? { ...p, pinned: true } : p)
      );
    } catch {
      // Non-critical
    } finally {
      setPinningID(null);
    }
  };

  const handleUnpin = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuID(null);
    setPinningID(project.projectID);
    try {
      const projectRef = doc(db, "users", userID, "projects", project.projectID);
      await updateDoc(projectRef, { pinned: false });
      setProjects((prev) =>
        prev.map((p) => p.projectID === project.projectID ? { ...p, pinned: false } : p)
      );
    } catch {
      // Non-critical
    } finally {
      setPinningID(null);
    }
  };

  const handleSetStatus = async (project: Project, status: ProjectStatus, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuID(null);
    setStatusMenuID(null);
    try {
      const projectRef = doc(db, "users", userID, "projects", project.projectID);
      await updateDoc(projectRef, { projectStatus: status });
      setProjects((prev) =>
        prev.map((p) =>
          p.projectID === project.projectID ? { ...p, projectStatus: status } : p
        )
      );
    } catch {
      // Non-critical
    }
  };

  const handleToggleTag = async (project: Project, tag: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const currentTags = project.tags ?? [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];
    try {
      const projectRef = doc(db, "users", userID, "projects", project.projectID);
      await updateDoc(projectRef, { tags: newTags });
      setProjects((prev) =>
        prev.map((p) =>
          p.projectID === project.projectID ? { ...p, tags: newTags } : p
        )
      );
    } catch {
      // Non-critical
    }
  };

  const handleExportSummary = () => {
    const today = new Date().toISOString().split("T")[0];
    const sorted = activeProjects
      .slice()
      .sort((a, b) => (a.projectTitle ?? "").localeCompare(b.projectTitle ?? ""));
    const rows = sorted.map((p) => {
      const title = (p.projectTitle || "Untitled Project").replace(/\|/g, "\\|");
      const status = p.projectStatus ?? "—";
      const steps = `${p.projectProgress ?? 0}/24`;
      const score = `${Math.min(p.projectProgress ?? 0, 100)}%`;
      const active = p.lastActiveAt ? timeAgo(p.lastActiveAt) : "—";
      return `| ${title} | ${status} | ${steps} | ${score} | ${active} |`;
    });
    const content = [
      `# Projects Summary — ${today}`,
      "",
      `_${sorted.length} active project${sorted.length !== 1 ? "s" : ""}_`,
      "",
      "| Project | Status | Steps | Score | Last Active |",
      "|---------|--------|-------|-------|-------------|",
      ...rows,
      "",
    ].join("\n");
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `projects-summary-${today}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleMenu = (projectID: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setStatusMenuID(null);
    setTagMenuID(null);
    setOpenMenuID((prev) => (prev === projectID ? null : projectID));
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuID(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeProjects = projects.filter((p) => !p.archivedAt);
  const archivedProjects = projects.filter((p) => !!p.archivedAt);
  const pinnedCount = activeProjects.filter((p) => p.pinned).length;
  const isAtProjectLimit = tier === "free" && activeProjects.length >= projectLimit;

  const handleNewProject = () => {
    if (isAtProjectLimit) {
      navigate("/profile/subscription?utm_source=project_limit");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    const ideaInput = document.querySelector<HTMLInputElement>(
      'input[placeholder="Describe your business idea..."]'
    );
    if (ideaInput) {
      setTimeout(() => ideaInput.focus(), 400);
    }
  };

  // Client-side search + tier + tag filter (only applied to active view)
  const baseList = showArchived ? archivedProjects : activeProjects;
  const filteredProjects = baseList
    .filter((project) => {
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (project.projectTitle ?? "").toLowerCase().includes(q) ||
        (project.projectDescription ?? "").toLowerCase().includes(q);
      if (showArchived) return matchesSearch;
      const progress = Math.min(project.projectProgress ?? 0, 100);
      const matchesTier =
        filterTier === "all" ||
        (filterTier === "strong" && progress >= 70) ||
        (filterTier === "needs-work" && progress >= 40 && progress < 70) ||
        (filterTier === "early" && progress < 40);
      const matchesTag =
        !activeTagFilter || (project.tags ?? []).includes(activeTagFilter);
      return matchesSearch && matchesTier && matchesTag;
    })
    .sort((a, b) => {
      // Pinned projects always appear first (active view only)
      if (showArchived) return 0;
      const ap = a.pinned ? 1 : 0;
      const bp = b.pinned ? 1 : 0;
      return bp - ap;
    });

  // Count how many pinned projects are in the filtered list (for divider rendering)
  const filteredPinnedCount = filteredProjects.filter((p) => p.pinned).length;

  // All tags that are used across active projects (for filter row)
  const usedTags = Array.from(
    new Set(activeProjects.flatMap((p) => p.tags ?? []))
  ).filter((t) => TAG_OPTIONS.some((o) => o.key === t));

  const showSearchBar = !loading && activeProjects.length >= 3 && !showArchived;

  // Preset helpers
  const isFiltersDefault = filterTier === "all" && activeTagFilter === null;
  const activePresetID = presets.find(
    (p) => p.filterTier === filterTier && p.tagFilter === activeTagFilter
  )?.id ?? null;
  const canSavePreset =
    !isFiltersDefault &&
    activePresetID === null &&
    !savingPreset &&
    presets.length < MAX_PRESETS;

  return (
    <>
      <div
        className={`card ${className}`}
        style={{
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          background: "#FFFFFF",
        }}
      >
        {/* Header */}
        <div
          className="card-header border-0 d-flex align-items-center justify-content-between"
          style={{ padding: "20px 24px 0" }}
        >
          <div>
            <span
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#0F172A",
                lineHeight: "1.4",
              }}
            >
              My Projects
            </span>
            {!loading && tier === "free" && (
              <span
                className="ms-2"
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  padding: "2px 8px",
                  borderRadius: "4px",
                  background: isAtProjectLimit ? "#FEE2E2" : "#F1F5F9",
                  color: isAtProjectLimit ? "#EF4444" : "#94A3B8",
                  letterSpacing: "0.02em",
                }}
              >
                {activeProjects.length} of {projectLimit} project{projectLimit !== 1 ? "s" : ""} used
              </span>
            )}
            {!loading && tier !== "free" && activeProjects.length > 0 && (
              <span
                className="ms-2"
                style={{ fontSize: "13px", fontWeight: 500, color: "#94A3B8" }}
              >
                {activeProjects.length} project{activeProjects.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {!loading && activeProjects.length > 0 && (
              <button
                className="btn btn-sm d-flex align-items-center gap-1"
                onClick={handleExportSummary}
                title="Download summary of all active projects as Markdown"
                style={{
                  background: "transparent",
                  color: "#475569",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  fontSize: "13px",
                  fontWeight: 500,
                  border: "1px solid #E2E8F0",
                  transition: "all 0.15s ease-out",
                }}
                onMouseEnter={(e) => {
                  const btn = e.currentTarget as HTMLButtonElement;
                  btn.style.background = "#F8FAFC";
                  btn.style.borderColor = "#CBD5E1";
                  btn.style.color = "#0F172A";
                }}
                onMouseLeave={(e) => {
                  const btn = e.currentTarget as HTMLButtonElement;
                  btn.style.background = "transparent";
                  btn.style.borderColor = "#E2E8F0";
                  btn.style.color = "#475569";
                }}
              >
                <i className="bi bi-download" style={{ fontSize: 14 }}></i>
                Export
              </button>
            )}
            {!readOnly && (
              <button
                className="btn btn-sm d-flex align-items-center gap-1"
                onClick={handleNewProject}
                style={{
                  background: isAtProjectLimit ? "#F59E0B" : "#3B82F6",
                  color: "white",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: 600,
                  border: "none",
                  transition: "background 0.15s ease-out",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = isAtProjectLimit
                    ? "#D97706"
                    : "#1D4ED8";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = isAtProjectLimit
                    ? "#F59E0B"
                    : "#3B82F6";
                }}
              >
                {isAtProjectLimit ? (
                  <>
                    <i className="bi bi-arrow-up-circle" style={{ fontSize: 16 }}></i>
                    Upgrade
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus" style={{ fontSize: 16 }}></i>
                    New Project
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Search + Filter bar */}
        {showSearchBar && (
          <div style={{ padding: "12px 24px 0" }}>
            {/* Search input */}
            <div className="position-relative mb-2">
              <i
                className="bi bi-search position-absolute"
                style={{
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94A3B8",
                  fontSize: 14,
                  pointerEvents: "none",
                }}
              />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  paddingLeft: 32,
                  paddingRight: searchQuery ? 32 : 12,
                  paddingTop: 7,
                  paddingBottom: 7,
                  fontSize: 13,
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  outline: "none",
                  color: "#0F172A",
                  background: "#F8FAFC",
                  transition: "border-color 0.15s ease-out",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#3B82F6")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#E2E8F0")}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    color: "#94A3B8",
                    cursor: "pointer",
                    padding: "2px 4px",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            {/* Filter chips + Save preset button */}
            <div className="d-flex align-items-center justify-content-between gap-1 flex-wrap">
              <div className="d-flex gap-1 flex-wrap">
                {FILTER_OPTIONS.map((opt) => {
                  const isActive = filterTier === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setFilterTier(opt.key)}
                      style={{
                        padding: "3px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: isActive ? 600 : 500,
                        border: `1px solid ${isActive ? "#3B82F6" : "#E2E8F0"}`,
                        background: isActive ? "#DBEAFE" : "#F8FAFC",
                        color: isActive ? "#1D4ED8" : "#475569",
                        cursor: "pointer",
                        transition: "all 0.15s ease-out",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Save preset inline input or button */}
              {savingPreset ? (
                <div className="d-flex align-items-center gap-1">
                  <input
                    ref={presetInputRef}
                    type="text"
                    placeholder="Preset name…"
                    value={presetInputValue}
                    onChange={(e) => setPresetInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSavePreset();
                      if (e.key === "Escape") { setSavingPreset(false); setPresetInputValue(""); }
                    }}
                    style={{
                      fontSize: 11,
                      padding: "3px 8px",
                      borderRadius: 6,
                      border: "1px solid #3B82F6",
                      outline: "none",
                      color: "#0F172A",
                      background: "#F8FAFC",
                      width: 120,
                    }}
                  />
                  <button
                    onClick={handleSavePreset}
                    disabled={!presetInputValue.trim()}
                    title="Save preset"
                    style={{
                      background: "#3B82F6",
                      border: "none",
                      borderRadius: 6,
                      color: "white",
                      fontSize: 12,
                      padding: "3px 7px",
                      cursor: presetInputValue.trim() ? "pointer" : "default",
                      opacity: presetInputValue.trim() ? 1 : 0.4,
                    }}
                  >
                    <i className="bi bi-check2"></i>
                  </button>
                  <button
                    onClick={() => { setSavingPreset(false); setPresetInputValue(""); }}
                    title="Cancel"
                    style={{
                      background: "transparent",
                      border: "1px solid #E2E8F0",
                      borderRadius: 6,
                      color: "#94A3B8",
                      fontSize: 12,
                      padding: "3px 7px",
                      cursor: "pointer",
                    }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
              ) : canSavePreset ? (
                <button
                  onClick={() => setSavingPreset(true)}
                  title="Save current filters as a preset"
                  style={{
                    background: "transparent",
                    border: "1px solid #E2E8F0",
                    borderRadius: 6,
                    color: "#94A3B8",
                    fontSize: 11,
                    padding: "3px 8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    transition: "all 0.15s ease-out",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    const btn = e.currentTarget as HTMLButtonElement;
                    btn.style.borderColor = "#CBD5E1";
                    btn.style.color = "#475569";
                  }}
                  onMouseLeave={(e) => {
                    const btn = e.currentTarget as HTMLButtonElement;
                    btn.style.borderColor = "#E2E8F0";
                    btn.style.color = "#94A3B8";
                  }}
                >
                  <i className="bi bi-bookmark" style={{ fontSize: 11 }}></i>
                  Save
                </button>
              ) : null}
            </div>

            {/* Tag filter chips — only shown when at least 1 project has tags */}
            {usedTags.length > 0 && (
              <div className="d-flex gap-1 flex-wrap mt-1">
                {usedTags.map((tag) => {
                  const isActive = activeTagFilter === tag;
                  return (
                    <button
                      key={tag}
                      onClick={() => setActiveTagFilter(isActive ? null : tag)}
                      style={{
                        padding: "3px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: isActive ? 600 : 500,
                        border: `1px solid ${isActive ? "#3B82F6" : "#E2E8F0"}`,
                        background: isActive ? "#DBEAFE" : "#F8FAFC",
                        color: isActive ? "#1D4ED8" : "#475569",
                        cursor: "pointer",
                        transition: "all 0.15s ease-out",
                      }}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Saved filter presets — shown when at least 1 preset exists */}
            {presets.length > 0 && (
              <div className="d-flex align-items-center gap-1 flex-wrap mt-1">
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: "#94A3B8",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    marginRight: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  Saved:
                </span>
                {presets.map((preset) => {
                  const isActive = activePresetID === preset.id;
                  return (
                    <div
                      key={preset.id}
                      className="d-flex align-items-center"
                      style={{
                        borderRadius: 999,
                        border: `1px solid ${isActive ? "#3B82F6" : "#E2E8F0"}`,
                        background: isActive ? "#DBEAFE" : "#F1F5F9",
                        overflow: "hidden",
                      }}
                    >
                      <button
                        onClick={() => handleApplyPreset(preset)}
                        style={{
                          padding: "3px 8px",
                          fontSize: 11,
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#1D4ED8" : "#475569",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <i
                          className={isActive ? "bi bi-bookmark-fill" : "bi bi-bookmark"}
                          style={{ fontSize: 10 }}
                        ></i>
                        {preset.name}
                      </button>
                      <button
                        onClick={(e) => handleDeletePreset(preset.id, e)}
                        title="Delete preset"
                        style={{
                          padding: "3px 6px 3px 0",
                          fontSize: 12,
                          color: isActive ? "#1D4ED8" : "#94A3B8",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          lineHeight: 1,
                          opacity: 0.7,
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.7"; }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Body */}
        <div className="card-body" style={{ padding: "16px 24px 24px" }}>
          {loading ? (
            /* Loading skeleton */
            <div className="d-flex flex-column gap-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="d-flex align-items-center gap-3"
                  style={{
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #E2E8F0",
                    background: "#F8FAFC",
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: "#E2E8F0",
                      flexShrink: 0,
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                  <div className="flex-grow-1">
                    <div
                      style={{
                        height: 14,
                        width: "60%",
                        background: "#E2E8F0",
                        borderRadius: 4,
                        marginBottom: 10,
                      }}
                    />
                    <div
                      style={{
                        height: 6,
                        width: "100%",
                        background: "#E2E8F0",
                        borderRadius: 9999,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : showArchived && archivedProjects.length === 0 ? (
            <div
              className="text-center d-flex flex-column align-items-center"
              style={{ padding: "24px 16px" }}
            >
              <i className="bi bi-archive d-block mb-2" style={{ fontSize: 36, color: "#94A3B8" }}></i>
              <p style={{ fontSize: "14px", color: "#475569", margin: 0 }}>
                No archived projects.
              </p>
            </div>
          ) : !showArchived && activeProjects.length === 0 ? (
            /* Empty state */
            <div
              className="text-center d-flex flex-column align-items-center"
              style={{ padding: "32px 16px" }}
            >
              <i
                className="bi bi-folder2-open d-block mb-3"
                style={{ fontSize: 48, color: "#94A3B8" }}
              ></i>
              <h5
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#0F172A",
                  marginBottom: 8,
                }}
              >
                No projects yet
              </h5>
              <p
                style={{
                  fontSize: "15px",
                  color: "#475569",
                  maxWidth: 320,
                  margin: "0 auto 20px",
                  lineHeight: 1.6,
                }}
              >
                Describe your business idea above to start your first 24-step validation.
              </p>
              <button
                className="btn"
                onClick={handleNewProject}
                style={{
                  background: "#3B82F6",
                  color: "white",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: 600,
                  border: "none",
                }}
              >
                <i className="bi bi-arrow-up me-2"></i>
                Start Your First Project
              </button>
            </div>
          ) : filteredProjects.length === 0 ? (
            /* No search results */
            <div
              className="text-center d-flex flex-column align-items-center"
              style={{ padding: "24px 16px" }}
            >
              <i
                className="bi bi-search d-block mb-2"
                style={{ fontSize: 36, color: "#94A3B8" }}
              ></i>
              <p style={{ fontSize: "14px", color: "#475569", margin: 0 }}>
                No projects match your search.
              </p>
              <button
                className="btn btn-sm mt-2"
                onClick={clearFilters}
                style={{
                  fontSize: 12,
                  color: "#3B82F6",
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            /* Project cards */
            <div className="d-flex flex-column gap-3">
              {filteredProjects.map((project, index) => {
                const progress = Math.min(project.projectProgress ?? 0, 100);
                const scoreStyle = getScoreStyle(progress);
                const isBeingDeleted = deletingID === project.projectID;
                // Render a divider after the last pinned project
                const showPinnedDivider =
                  !showArchived &&
                  filteredPinnedCount > 0 &&
                  filteredPinnedCount < filteredProjects.length &&
                  index === filteredPinnedCount - 1;

                return (
                  <React.Fragment key={project.projectID}>
                  <div
                    className="d-flex align-items-center gap-3"
                    role="button"
                    tabIndex={0}
                    onClick={() => !isBeingDeleted && handleNavigate(project)}
                    onKeyDown={(e) => e.key === "Enter" && !isBeingDeleted && handleNavigate(project)}
                    style={{
                      padding: "16px",
                      borderRadius: "12px",
                      border: project.pinned ? "1px solid #BFDBFE" : "1px solid #E2E8F0",
                      background: isBeingDeleted ? "#FEF2F2" : "#FFFFFF",
                      cursor: isBeingDeleted ? "default" : "pointer",
                      opacity: isBeingDeleted ? 0.6 : 1,
                      transition: "box-shadow 0.15s ease-out, border-color 0.15s ease-out, opacity 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (isBeingDeleted) return;
                      e.currentTarget.style.boxShadow =
                        "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)";
                      e.currentTarget.style.borderColor = "#CBD5E1";
                    }}
                    onMouseLeave={(e) => {
                      if (isBeingDeleted) return;
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.borderColor = project.pinned ? "#BFDBFE" : "#E2E8F0";
                    }}
                  >
                    {/* Score circle */}
                    <div
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background: scoreStyle.bg,
                        border: `2px solid ${scoreStyle.color}`,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: scoreStyle.color,
                          fontFamily: "JetBrains Mono, monospace",
                          letterSpacing: "0",
                        }}
                      >
                        {progress}%
                      </span>
                    </div>

                    {/* Project info */}
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center gap-2 min-w-0 me-2">
                          <span
                            className="text-truncate"
                            style={{
                              fontSize: "15px",
                              fontWeight: 600,
                              color: project.archivedAt ? "#94A3B8" : "#0F172A",
                            }}
                          >
                            {project.projectTitle || "Untitled Project"}
                          </span>
                          {project.pinned && (
                            <i
                              className="bi bi-pin-angle-fill flex-shrink-0"
                              style={{ fontSize: 12, color: "#3B82F6" }}
                              title="Pinned"
                            ></i>
                          )}
                          {project.archivedAt && (
                            <span
                              className="flex-shrink-0"
                              style={{
                                fontSize: "11px",
                                fontWeight: 500,
                                padding: "2px 8px",
                                borderRadius: "4px",
                                background: "#FEF3C7",
                                color: "#92400E",
                                letterSpacing: "0.02em",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Archived
                            </span>
                          )}
                        </div>
                        {!project.archivedAt && (
                          <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            {/* Score label with delta arrow merged */}
                            {(() => {
                              const delta = scoreDeltas[project.projectID];
                              const isAlert = healthAlerts[project.projectID];
                              const hasDelta = delta !== undefined && delta !== 0;
                              return (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: 500,
                                    padding: "2px 8px",
                                    borderRadius: "4px",
                                    background: scoreStyle.bg,
                                    color: scoreStyle.color,
                                    letterSpacing: "0.02em",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3,
                                  }}
                                  title={
                                    isAlert
                                      ? "Score dropped more than 5 points since last session"
                                      : hasDelta
                                      ? `Score ${delta! > 0 ? "up" : "down"} ${Math.abs(delta!)} points since last session`
                                      : undefined
                                  }
                                >
                                  {scoreStyle.label}
                                  {isAlert && (
                                    <i className="bi bi-arrow-down" style={{ fontSize: 9, color: "#EF4444", marginLeft: 2 }}></i>
                                  )}
                                  {!isAlert && hasDelta && (
                                    <i
                                      className={`bi ${delta! > 0 ? "bi-arrow-up" : "bi-arrow-down"}`}
                                      style={{ fontSize: 9, marginLeft: 2 }}
                                    ></i>
                                  )}
                                </span>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Progress bar */}
                      <div
                        style={{
                          height: 6,
                          background: "#F1F5F9",
                          borderRadius: 9999,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${progress}%`,
                            background: scoreStyle.color,
                            borderRadius: 9999,
                            transition: "width 0.4s ease-out",
                            minWidth: progress > 0 ? 6 : 0,
                          }}
                        />
                      </div>

                      {/* Step count + last active timestamp */}
                      <div style={{ marginTop: 4, fontSize: 11, color: "#94A3B8" }}>
                        {project.projectProgress ?? 0}/24 steps
                        {project.lastActiveAt && (
                          <> · Active {timeAgo(project.lastActiveAt)}</>
                        )}
                      </div>

                      {/* Status badge */}
                      {project.projectStatus && (
                        <div style={{ marginTop: 6 }}>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 500,
                              padding: "2px 8px",
                              borderRadius: "9999px",
                              background: getStatusStyle(project.projectStatus).bg,
                              color: getStatusStyle(project.projectStatus).color,
                              letterSpacing: "0.02em",
                            }}
                          >
                            {project.projectStatus === "Launched" && "🚀 "}
                            {project.projectStatus}
                          </span>
                        </div>
                      )}

                      {/* Tag chips — up to 3, neutral style */}
                      {(project.tags ?? []).length > 0 && (
                        <div className="d-flex flex-wrap gap-1" style={{ marginTop: 6 }}>
                          {(project.tags ?? []).slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              style={{
                                fontSize: "11px",
                                fontWeight: 500,
                                padding: "2px 8px",
                                borderRadius: "9999px",
                                background: "#F1F5F9",
                                color: "#475569",
                                letterSpacing: "0.02em",
                                border: "1px solid #E2E8F0",
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Team badge — shown when project is shared with team */}
                      {project.teamId && (
                        <div style={{ marginTop: 6 }}>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 500,
                              padding: "2px 8px",
                              borderRadius: "9999px",
                              background: "#EDE9FE",
                              color: "#6D28D9",
                              letterSpacing: "0.02em",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                            title="Shared with team"
                          >
                            <i className="bi bi-people-fill" style={{ fontSize: 10 }} />
                            Team
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions: 3-dot menu + chevron */}
                    {!readOnly && (
                    <div
                      className="d-flex align-items-center gap-1 flex-shrink-0"
                      ref={openMenuID === project.projectID ? menuRef : null}
                    >
                      {isBeingDeleted || archivingID === project.projectID || pinningID === project.projectID ? (
                        <div
                          className="spinner-border spinner-border-sm me-1"
                          style={{ color: isBeingDeleted ? "#EF4444" : pinningID === project.projectID ? "#3B82F6" : "#F59E0B", width: 16, height: 16, borderWidth: 2 }}
                        ></div>
                      ) : duplicatingID === project.projectID ? (
                        <div
                          className="spinner-border spinner-border-sm me-1"
                          style={{ color: "#94A3B8", width: 16, height: 16, borderWidth: 2 }}
                        ></div>
                      ) : (
                        <div className="position-relative">
                          <button
                            className="btn btn-icon btn-sm"
                            onClick={(e) => toggleMenu(project.projectID, e)}
                            title="Project actions"
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 6,
                              border: "1px solid transparent",
                              background: "transparent",
                              color: "#94A3B8",
                              transition: "background 0.15s ease-out",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#F1F5F9";
                              e.currentTarget.style.borderColor = "#E2E8F0";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                              e.currentTarget.style.borderColor = "transparent";
                            }}
                          >
                            <i className="bi bi-three-dots-vertical" style={{ fontSize: 14 }}></i>
                          </button>

                          {openMenuID === project.projectID && (
                            <div
                              style={{
                                position: "absolute",
                                top: "calc(100% + 4px)",
                                right: 0,
                                zIndex: 100,
                                background: "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                borderRadius: 8,
                                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
                                minWidth: 160,
                                padding: "4px 0",
                              }}
                            >
                              {!project.archivedAt && project.pinned && (
                                <button
                                  className="w-100 d-flex align-items-center gap-2 px-3 py-2"
                                  onClick={(e) => handleUnpin(project, e)}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: "#3B82F6",
                                    transition: "background 0.1s",
                                    textAlign: "left",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#EFF6FF";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                  }}
                                >
                                  <i className="bi bi-pin-angle-fill" style={{ fontSize: 14, color: "#3B82F6" }}></i>
                                  Unpin
                                </button>
                              )}
                              {!project.archivedAt && !project.pinned && (
                                <button
                                  className="w-100 d-flex align-items-center gap-2 px-3 py-2"
                                  onClick={pinnedCount >= 3 ? undefined : (e) => handlePin(project, e)}
                                  title={pinnedCount >= 3 ? "You can pin up to 3 projects" : "Pin to top of list"}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: pinnedCount >= 3 ? "default" : "pointer",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: pinnedCount >= 3 ? "#CBD5E1" : "#0F172A",
                                    opacity: pinnedCount >= 3 ? 0.5 : 1,
                                    transition: "background 0.1s",
                                    textAlign: "left",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (pinnedCount < 3) e.currentTarget.style.background = "#F8FAFC";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                  }}
                                >
                                  <i className="bi bi-pin-angle" style={{ fontSize: 14, color: pinnedCount >= 3 ? "#CBD5E1" : "#475569" }}></i>
                                  Pin to top
                                </button>
                              )}
                              {!project.archivedAt && (
                                <button
                                  className="w-100 d-flex align-items-center gap-2 px-3 py-2"
                                  onClick={(e) => handleDuplicate(project, e)}
                                  disabled={isDuplicating}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: "#0F172A",
                                    transition: "background 0.1s",
                                    textAlign: "left",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#F8FAFC";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                  }}
                                >
                                  <i className="bi bi-copy" style={{ fontSize: 14, color: "#475569" }}></i>
                                  Duplicate (Copy)
                                </button>
                              )}
                              {/* Set Status submenu */}
                              <div
                                style={{ position: "relative" }}
                                onMouseEnter={() => setStatusMenuID(project.projectID)}
                                onMouseLeave={() => setStatusMenuID(null)}
                              >
                                <button
                                  className="w-100 d-flex align-items-center justify-content-between px-3 py-2"
                                  style={{
                                    background: statusMenuID === project.projectID ? "#F8FAFC" : "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: "#0F172A",
                                    transition: "background 0.1s",
                                    textAlign: "left",
                                  }}
                                >
                                  <span className="d-flex align-items-center gap-2">
                                    <i className="bi bi-tag" style={{ fontSize: 14, color: "#475569" }}></i>
                                    Set Status
                                  </span>
                                  <i className="bi bi-chevron-right" style={{ fontSize: 11, color: "#94A3B8" }}></i>
                                </button>
                                {statusMenuID === project.projectID && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      left: "calc(100% + 2px)",
                                      zIndex: 101,
                                      background: "#FFFFFF",
                                      border: "1px solid #E2E8F0",
                                      borderRadius: 8,
                                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
                                      minWidth: 130,
                                      padding: "4px 0",
                                    }}
                                  >
                                    {STATUS_OPTIONS.map((opt) => (
                                      <button
                                        key={opt.key}
                                        className="w-100 d-flex align-items-center gap-2 px-3 py-2"
                                        onClick={(e) => handleSetStatus(project, opt.key, e)}
                                        style={{
                                          background: project.projectStatus === opt.key ? opt.bg : "transparent",
                                          border: "none",
                                          cursor: "pointer",
                                          fontSize: 13,
                                          fontWeight: project.projectStatus === opt.key ? 600 : 500,
                                          color: opt.color,
                                          transition: "background 0.1s",
                                          textAlign: "left",
                                        }}
                                        onMouseEnter={(e) => {
                                          if (project.projectStatus !== opt.key)
                                            e.currentTarget.style.background = opt.bg;
                                        }}
                                        onMouseLeave={(e) => {
                                          if (project.projectStatus !== opt.key)
                                            e.currentTarget.style.background = "transparent";
                                        }}
                                      >
                                        <span
                                          style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            background: opt.color,
                                            flexShrink: 0,
                                          }}
                                        />
                                        {opt.key}
                                        {project.projectStatus === opt.key && (
                                          <i className="bi bi-check2 ms-auto" style={{ fontSize: 13, color: opt.color }}></i>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {/* Tags submenu */}
                              <div
                                style={{ position: "relative" }}
                                onMouseEnter={() => setTagMenuID(project.projectID)}
                                onMouseLeave={() => setTagMenuID(null)}
                              >
                                <button
                                  className="w-100 d-flex align-items-center justify-content-between px-3 py-2"
                                  style={{
                                    background: tagMenuID === project.projectID ? "#F8FAFC" : "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: "#0F172A",
                                    transition: "background 0.1s",
                                    textAlign: "left",
                                  }}
                                >
                                  <span className="d-flex align-items-center gap-2">
                                    <i className="bi bi-bookmark" style={{ fontSize: 14, color: "#475569" }}></i>
                                    Tags
                                    {(project.tags ?? []).length > 0 && (
                                      <span style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        padding: "1px 6px",
                                        borderRadius: 999,
                                        background: "#DBEAFE",
                                        color: "#3B82F6",
                                      }}>
                                        {(project.tags ?? []).length}
                                      </span>
                                    )}
                                  </span>
                                  <i className="bi bi-chevron-right" style={{ fontSize: 11, color: "#94A3B8" }}></i>
                                </button>
                                {tagMenuID === project.projectID && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      left: "calc(100% + 2px)",
                                      zIndex: 101,
                                      background: "#FFFFFF",
                                      border: "1px solid #E2E8F0",
                                      borderRadius: 8,
                                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
                                      minWidth: 150,
                                      padding: "4px 0",
                                    }}
                                  >
                                    {TAG_OPTIONS.map((opt) => {
                                      const isSelected = (project.tags ?? []).includes(opt.key);
                                      return (
                                        <button
                                          key={opt.key}
                                          className="w-100 d-flex align-items-center gap-2 px-3 py-2"
                                          onClick={(e) => handleToggleTag(project, opt.key, e)}
                                          style={{
                                            background: isSelected ? opt.bg : "transparent",
                                            border: "none",
                                            cursor: "pointer",
                                            fontSize: 13,
                                            fontWeight: isSelected ? 600 : 500,
                                            color: isSelected ? opt.color : "#475569",
                                            transition: "background 0.1s",
                                            textAlign: "left",
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isSelected) e.currentTarget.style.background = opt.bg + "80";
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isSelected) e.currentTarget.style.background = "transparent";
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              background: opt.color,
                                              flexShrink: 0,
                                            }}
                                          />
                                          {opt.key}
                                          {isSelected && (
                                            <i className="bi bi-check2 ms-auto" style={{ fontSize: 13, color: opt.color }}></i>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              {project.archivedAt ? (
                                <button
                                  className="w-100 d-flex align-items-center gap-2 px-3 py-2"
                                  onClick={(e) => handleUnarchive(project, e)}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: "#F59E0B",
                                    transition: "background 0.1s",
                                    textAlign: "left",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#FFFBEB";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                  }}
                                >
                                  <i className="bi bi-archive" style={{ fontSize: 14, color: "#F59E0B" }}></i>
                                  Unarchive
                                </button>
                              ) : (
                                <button
                                  className="w-100 d-flex align-items-center gap-2 px-3 py-2"
                                  onClick={(e) => handleArchive(project, e)}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: "#475569",
                                    transition: "background 0.1s",
                                    textAlign: "left",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#F8FAFC";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                  }}
                                >
                                  <i className="bi bi-archive" style={{ fontSize: 14, color: "#F59E0B" }}></i>
                                  Archive
                                </button>
                              )}
                              <div style={{ height: 1, background: "#F1F5F9", margin: "2px 0" }} />
                              <button
                                className="w-100 d-flex align-items-center gap-2 px-3 py-2"
                                onClick={(e) => handleDeleteRequest(project, e)}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#EF4444",
                                  transition: "background 0.1s",
                                  textAlign: "left",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#FEF2F2";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "transparent";
                                }}
                              >
                                <i className="bi bi-trash3" style={{ fontSize: 14, color: "#EF4444" }}></i>
                                Delete Project
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <i
                        className="bi bi-chevron-right"
                        style={{ fontSize: 16, color: "#94A3B8" }}
                      ></i>
                    </div>
                    )}
                  </div>
                  {showPinnedDivider && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: -4 }}>
                      <div style={{ flex: 1, height: 1, background: "#E2E8F0" }} />
                      <span style={{ fontSize: 10, fontWeight: 500, color: "#CBD5E1", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
                        OTHER PROJECTS
                      </span>
                      <div style={{ flex: 1, height: 1, background: "#E2E8F0" }} />
                    </div>
                  )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Archive toggle footer */}
        {!loading && archivedProjects.length > 0 && (
          <div
            style={{
              padding: "8px 24px 16px",
              borderTop: "1px solid #F1F5F9",
            }}
          >
            <button
              onClick={() => { setShowArchived((prev) => !prev); setSearchQuery(""); setFilterTier("all"); setActiveTagFilter(null); }}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 500,
                color: "#94A3B8",
                padding: 0,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#475569")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#94A3B8")}
            >
              <i className={`bi ${showArchived ? "bi-eye-slash" : "bi-archive"}`} style={{ fontSize: 13 }}></i>
              {showArchived
                ? `Show active (${activeProjects.length})`
                : `Show archived (${archivedProjects.length})`}
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDeleteProject && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1060,
            background: "rgba(15, 23, 42, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
          onClick={() => setConfirmDeleteProject(null)}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: "28px 32px",
              maxWidth: 420,
              width: "100%",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex align-items-center gap-3 mb-4">
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="bi bi-trash3" style={{ fontSize: 20, color: "#EF4444" }}></i>
              </div>
              <div>
                <h5 style={{ fontSize: 17, fontWeight: 600, color: "#0F172A", margin: 0 }}>
                  Delete project?
                </h5>
                <p style={{ fontSize: 13, color: "#475569", margin: "2px 0 0" }}>
                  This cannot be undone.
                </p>
              </div>
            </div>

            <p style={{ fontSize: 14, color: "#475569", marginBottom: 24 }}>
              <strong style={{ color: "#0F172A" }}>
                "{confirmDeleteProject.projectTitle || "Untitled Project"}"
              </strong>{" "}
              and all its step data will be permanently deleted.
            </p>

            <div className="d-flex gap-2 justify-content-end">
              <button
                onClick={() => setConfirmDeleteProject(null)}
                style={{
                  padding: "8px 18px",
                  borderRadius: 8,
                  border: "1px solid #E2E8F0",
                  background: "#FFFFFF",
                  color: "#475569",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "background 0.15s ease-out",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: "8px 18px",
                  borderRadius: 8,
                  border: "none",
                  background: "#EF4444",
                  color: "#FFFFFF",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 0.15s ease-out",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#DC2626")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#EF4444")}
              >
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { ProjectList };
